import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectIsRoundsAndSeriesSelected, selectIsSeasonSelected } from '../reducers/leagues_configs'
import {
  selectCloningStep,
  setCloningStep,
  setCloningModal,
} from '../reducers/cloning_steps'
import { CloningStepsEnum, CloningStepsModals } from '../constants'
import useSessionSaver from './useSessionSaver'
import { selectAllLeaguesRenamed, selectAllLeaguesHaveFullDates } from '../reducers/session_saver'
import { selectSelectedLeaguesForCloning } from '../reducers/selected_leagues_for_cloning'

const useCloningStepsActions = () => {
  const dispatch = useDispatch()
  const { startSessionJobs } = useSessionSaver()
  const roundsAndSeriesSelected = useSelector(selectIsRoundsAndSeriesSelected)
  const isSeasonSelected = useSelector(selectIsSeasonSelected)
  const cloningStep = useSelector(selectCloningStep)
  const allLeaguesRenamed = useSelector(selectAllLeaguesRenamed)
  const allLeaguesHaveFullDates = useSelector(selectAllLeaguesHaveFullDates)
  const selectedLeaguesForCloning = useSelector(selectSelectedLeaguesForCloning)

  const isLastCloningStep = useMemo(() => {
    if (roundsAndSeriesSelected && cloningStep === CloningStepsEnum.DATES) {
      return true
    }

    return cloningStep === CloningStepsEnum.DATES
  }, [ cloningStep, roundsAndSeriesSelected ])

  // Single source of truth for whether the user may advance from the current step
  const isNextStepAllowed = useMemo(() => {
    if (selectedLeaguesForCloning.length === 0) {
      return false
    }

    if (cloningStep === CloningStepsEnum.CONFIG && !isSeasonSelected) {
      return false
    }

    if (cloningStep === CloningStepsEnum.DATES && !allLeaguesHaveFullDates) {
      return false
    }

    return true
  }, [ cloningStep, isSeasonSelected, selectedLeaguesForCloning.length, allLeaguesHaveFullDates ])

  const goToPrevCloningStep = useCallback(() => {
    if (cloningStep === CloningStepsEnum.BROWSE) {
      dispatch(setCloningModal(CloningStepsModals.CANCEL_SESSION))
      return
    }

    if (cloningStep > 0) {
      dispatch(setCloningStep(cloningStep - 1))
    }
  }, [ cloningStep ])

  const goToNextCloningStep = useCallback(() => {
    if (!isNextStepAllowed) {
      return
    }

    if (!isLastCloningStep) {
      if (cloningStep === CloningStepsEnum.RENAME && !allLeaguesRenamed) {
        dispatch(setCloningModal(CloningStepsModals.MISSING_LEAGUES_RENAMES))
        return
      }

      dispatch(setCloningStep(cloningStep + 1))
    } else {
      startSessionJobs()
    }
  }, [ cloningStep, isLastCloningStep, allLeaguesRenamed, isNextStepAllowed, startSessionJobs ])

  const confirmNextCloningStep = useCallback(() => {
    dispatch(setCloningModal(null))
    dispatch(setCloningStep(cloningStep + 1))
  }, [ cloningStep ])

  return {
    goToNextCloningStep,
    confirmNextCloningStep,
    goToPrevCloningStep,
    isLastCloningStep,
    isNextStepAllowed,
  }
}

export default useCloningStepsActions
